import { type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useOrganizationContext } from '../../contexts/OrganizationContext';
import { useOrgAccess } from '../../../domains/organizations/hooks/useOrgPermissions';
import { hasMinimumRole, type OrganizationMemberRole } from '../../types/organization.types';
import type { Organization } from '../../types/organization.types';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface OrgGuardProps {
  children: ReactNode;
  /** Minimum role required to access this content (default: viewer) */
  minRole?: OrganizationMemberRole;
  /** Custom fallback component when access is denied */
  fallback?: ReactNode;
  /** Redirect path when access is denied */
  redirectTo?: string;
}

/**
 * Guard component that protects routes/components based on organization membership.
 * Automatically reads :slug from URL params to determine the organization.
 * Org data comes from bootstrap RPC (covers members and admins visiting by slug).
 *
 * Usage:
 * <Route path="org/:slug/*" element={
 *   <OrgGuard minRole="member">
 *     <OrgLayout />
 *   </OrgGuard>
 * } />
 */
export function OrgGuard({
  children,
  minRole = 'viewer',
  fallback,
  redirectTo,
}: OrgGuardProps) {
  const { slug } = useParams<{ slug: string }>();
  const {
    userContext,
    isLoading,
    bootstrapTargetOrg,
    isSystemAdmin,
  } = useOrganizationContext();

  // Org from bootstrap (includes admin + slug resolution)
  const orgToUse =
    bootstrapTargetOrg != null && bootstrapTargetOrg.organization?.slug === slug
      ? (bootstrapTargetOrg.organization as Organization)
      : userContext?.organizations.find((m) => m.organization.slug === slug)?.organization ?? null;
  
  // Use the access hook to check permissions
  const { hasAccess, role } = useOrgAccess(orgToUse?.id || null);

  // Show loading state while fetching bootstrap
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Loading Organization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              Loading...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if organization exists (slug not found for admin, or no membership for user)
  if (!isLoading && slug && !orgToUse) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Organization Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <p className="mb-2">The organization "{slug}" could not be found.</p>
              <p className="text-sm">
                Please check the URL and try again.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has access (system admins always have access)
  if (!hasAccess && !isSystemAdmin) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Organization Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <p className="mb-2">You don't have access to this organization.</p>
              <p className="text-sm">
                Please check the URL or contact an administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has minimum required role (system admins bypass this)
  if (role && !isSystemAdmin && !hasMinimumRole(role, minRole)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Insufficient Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <p className="mb-2">You don't have the required permissions.</p>
              <p className="text-sm">
                This action requires at least "{minRole}" role.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Guard for organization routes that require the user to be in ANY organization.
 * Redirects to org creation if user has no organizations.
 */
export function RequireAnyOrg({ 
  children,
  redirectTo = '/org/new'
}: { 
  children: ReactNode;
  redirectTo?: string;
}) {
  const { isLoading, isOrgMember, isSystemAdmin } = useOrganizationContext();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isOrgMember && !isSystemAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

